import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Deep-link action trigger (dashboard Quick Actions, Wave A).
 *
 * When the URL carries `?action=<value>` and `ready` is true, fires `run`
 * exactly once, then strips only the `action` key (other params — filters —
 * survive). Guarded so it never re-fires on re-render or history nav.
 *
 * @param value  the action value this page answers (e.g. 'add', 'record', 'pay')
 * @param ready  gate — pass a loaded flag so the flow opens only once data
 *               its drawer needs is present; pass true if there's no dependency
 * @param run    opens the page's primary flow
 */
export function useActionParam(value: string, ready: boolean, run: () => void) {
  const [searchParams, setSearchParams] = useSearchParams();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    if (searchParams.get('action') !== value) return;
    if (!ready) return;
    fired.current = true;
    run();
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, value, ready, run, setSearchParams]);
}

export default useActionParam;
